// teach style 14: an Adobe Animate HTML5 export served as ordinary static
// media. The page stores the entry file as a relative media path
// (`/private/html5/<folderId>/index.html`, HTML5_PREFIX); the browser unzips
// the admin's zip and uploads every file through the server
// (`POST /api/html5/:folderId/file?path=`), and the iframe URL is built with
// MEDIA_STORAGE exactly like an image. Pages saved before 2026-09-30 hold a
// full URL on the old (lost) CloudFront; `html5PageUrl` passes those through
// unchanged until `scripts/restoreHtml5Pages.js` on the server rewrites them.
import { MEDIA_STORAGE, IS_CN_REGION } from "../config/environment";

export const HTML5_PREFIX = "/private/html5/";

// The server's src/config/html5Export.js refuses these plus `.gz` (which
// never reaches it, see below). Only executables and Brotli builds are
// refused: an export may carry `.data` / `.wasm` / `.unityweb` (Unity
// WebGL), `.atlas`, files without an extension, and so on. A pre-compressed
// build (Unity without "Decompression Fallback") needs a Content-Encoding
// header that the Singapore copy loses (DataTransferHub drops metadata and
// the CloudFront Function that restores the Content-Type cannot set
// Content-Encoding), so `.gz` files are gunzipped here before the upload and
// stored under the plain name (`planHtml5Upload` / `prepareHtml5FileBytes`);
// the browser has no Brotli decoder, so `.br` is refused — publish with Gzip
// or with Decompression Fallback (`.unityweb`).
export const HTML5_BLOCKED_EXTENSIONS =
  /\.(exe|dll|msi|bat|cmd|com|scr|pif|ps1|vbs|vbe|wsf|sh|jar|apk|php|phtml|asp|aspx|jsp|cgi|br)$/i;

const GZIP_SUFFIX = /\.gz$/i;
// Files whose text names the compressed assets (Unity's index.html carries
// `dataUrl: buildUrl + "/Game.data.gz"`); they are rewritten to the plain
// names so the loader treats the assets as uncompressed.
const TEXT_WITH_ASSET_NAMES = /\.(html?|js|json)$/i;

export const HTML5_MAX_ZIP_BYTES = 300 * 1024 * 1024;
export const HTML5_MAX_FILES = 3000;
// One stored file; the server refuses more (HTML5_MAX_FILE_BYTES there).
// Matters for a gunzipped asset, whose size is only known after inflating.
export const HTML5_MAX_FILE_BYTES = 100 * 1024 * 1024;

const IGNORED_BASENAMES = ["", ".DS_Store", "Thumbs.db", "desktop.ini"];

const basename = (p) => p.slice(p.lastIndexOf("/") + 1);

const isJunk = (name) =>
  name.split("/").includes("__MACOSX") ||
  IGNORED_BASENAMES.includes(basename(name));

// Some Windows archivers write `\` separators; the server refuses those.
const normalizeZipPath = (name) => name.replace(/\\/g, "/");

const hasBadSegment = (name) =>
  name.split("/").some((s) => s === "" || s === "." || s === "..");

// Zip entry names without the UTF-8 flag are decoded by JSZip as UTF-8
// anyway, which turns a CP950 / GBK name from an old Windows archiver into
// mojibake, and the animation's manifest then asks for a file that was never
// uploaded. Strict UTF-8 first, then the region's legacy code page.
export const zipFileNameDecoder = (fallbackEncoding = IS_CN_REGION ? "gbk" : "big5") => {
  if (typeof TextDecoder === "undefined") return undefined;
  const strictUtf8 = new TextDecoder("utf-8", { fatal: true });
  let fallback = null;
  try {
    fallback = new TextDecoder(fallbackEncoding);
  } catch (error) {
    fallback = null;
  }
  return (bytes) => {
    try {
      return strictUtf8.decode(bytes);
    } catch (error) {
      return fallback ? fallback.decode(bytes) : new TextDecoder().decode(bytes);
    }
  };
};

export const isLegacyHtml5Url = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value);

export const isHtml5Src = (value) =>
  typeof value === "string" && value.startsWith(HTML5_PREFIX);

// The URL the iframe loads for a stored value; "" when there is nothing to show.
export const html5PageUrl = (value, base = MEDIA_STORAGE) => {
  if (isLegacyHtml5Url(value)) return value;
  if (isHtml5Src(value)) return `${base || ""}${encodeURI(value)}`;
  return "";
};

export const html5Src = (folderId, path) => `${HTML5_PREFIX}${folderId}/${path}`;

const depthOf = (name) => name.split("/").length;
const byDepth = (a, b) => depthOf(a) - depthOf(b);

// Decides what to upload from the zip's listing (`[{ path, dir }]`, as JSZip
// gives it). The entry is the shallowest index.html anywhere in the zip, or,
// when there is none, the shallowest other .html; everything below its
// folder is uploaded, junk (__MACOSX, .DS_Store), bad path segments and
// executables are reported in `skipped`. A `.gz` file is uploaded gunzipped
// under its plain name (`gunzip: true`; dropped as "duplicate" when the
// plain name is in the zip too) and `renames` lists the name changes the
// text files must be patched with.
// Returns { entryPath, files: [{ zipPath, path, gunzip }], renames,
// probePath, skipped } or throws an Error with `code` = "no-entry" |
// "too-many-files".
export const planHtml5Upload = (entries) => {
  const skipped = [];
  const names = [];
  for (const entry of entries) {
    if (entry.dir) continue;
    const name = normalizeZipPath(entry.path);
    if (isJunk(name)) continue;
    if (hasBadSegment(name)) {
      skipped.push({ zipPath: entry.path, reason: "path" });
      continue;
    }
    names.push({ zipPath: entry.path, name });
  }
  const htmls = names.map((n) => n.name).filter((n) => /\.html?$/i.test(n));
  const indexes = htmls.filter((n) => /^index\.html?$/i.test(basename(n)));
  const entry =
    indexes.sort(byDepth)[0] || htmls.sort(byDepth)[0] || null;
  if (!entry) {
    const error = new Error("The zip has no .html file");
    error.code = "no-entry";
    throw error;
  }
  const rootPrefix = entry.slice(0, entry.length - basename(entry).length);

  const files = [];
  const renames = [];
  const plainPaths = new Set(
    names
      .filter((n) => n.name.startsWith(rootPrefix))
      .map((n) => n.name.slice(rootPrefix.length))
      .filter((p) => !GZIP_SUFFIX.test(p))
  );
  for (const { zipPath, name } of names) {
    if (!name.startsWith(rootPrefix)) {
      skipped.push({ zipPath, reason: "outside-root" });
      continue;
    }
    const path = name.slice(rootPrefix.length);
    const gunzip = GZIP_SUFFIX.test(path);
    const stored = gunzip ? path.replace(GZIP_SUFFIX, "") : path;
    if (HTML5_BLOCKED_EXTENSIONS.test(stored)) {
      skipped.push({ zipPath, reason: "extension" });
      continue;
    }
    if (gunzip) {
      if (plainPaths.has(stored)) {
        skipped.push({ zipPath, reason: "duplicate" });
        continue;
      }
      renames.push({ from: basename(path), to: basename(stored) });
    }
    files.push({ zipPath, path: stored, gunzip });
  }
  if (files.length > HTML5_MAX_FILES) {
    const error = new Error(`The zip has more than ${HTML5_MAX_FILES} files`);
    error.code = "too-many-files";
    throw error;
  }

  // A non-html file to poll on the CDN after the upload: the site's
  // CloudFront rewrites a missing key to the SPA's index.html (text/html), so
  // only a file that is not html tells "synced" apart from "not yet". The
  // caller adds the file that finished uploading last (DataTransferHub copies
  // objects in no particular order).
  const probe =
    files.find((f) => /\.js$/i.test(f.path)) ||
    files.find((f) => isProbeableHtml5Path(f.path)) ||
    null;

  return {
    entryPath: entry.slice(rootPrefix.length),
    files,
    renames,
    probePath: probe ? probe.path : null,
    skipped
  };
};

// A file the CDN probe can judge by its content type: not html (a missing
// key answers with the SPA page, also html) and not something legitimately
// served as application/octet-stream (Unity's `.data`, `.unityweb`, files
// without an extension), which the probe would wait for for ever.
const PROBEABLE_EXTENSIONS =
  /\.(js|mjs|css|json|png|jpe?g|gif|svg|webp|mp3|ogg|wav|m4a|mp4|webm|wasm|woff2?|ttf|otf)$/i;
export const isProbeableHtml5Path = (path) => PROBEABLE_EXTENSIONS.test(path);

// The bytes to upload for one planned file: gunzipped when the zip held it
// compressed, and, for a text file, with every renamed asset referenced by
// its plain name. `gunzip` is injected (pako in the browser) so this stays
// testable. Throws an Error with `code` "bad-gzip" | "file-too-large" and
// `path` so the admin learns which file.
export const needsAssetNamePatch = (file, renames) =>
  renames.length > 0 && TEXT_WITH_ASSET_NAMES.test(file.path);

const fileError = (code, path, message) => {
  const error = new Error(message);
  error.code = code;
  error.path = path;
  return error;
};

export const prepareHtml5FileBytes = (bytes, file, renames, gunzip) => {
  let out = bytes;
  if (file.gunzip) {
    try {
      out = gunzip(bytes);
    } catch (cause) {
      // pako 1.x throws a bare string
      throw fileError("bad-gzip", file.path, `${file.path}.gz is not a valid gzip file (${cause})`);
    }
    if (out.length > HTML5_MAX_FILE_BYTES) {
      throw fileError(
        "file-too-large",
        file.path,
        `${file.path} is ${Math.round(out.length / 1024 / 1024)} MB once decompressed; the limit is ${HTML5_MAX_FILE_BYTES / 1024 / 1024} MB`
      );
    }
  }
  if (needsAssetNamePatch(file, renames)) {
    const text = new TextDecoder("utf-8").decode(out);
    // Only a file that really names a renamed asset is re-encoded; the
    // others keep their exact bytes (a Big5 file would otherwise be rewritten
    // with replacement characters).
    if (renames.some(({ from }) => text.includes(from))) {
      let patched = text;
      for (const { from, to } of renames) {
        patched = patched.split(from).join(to);
      }
      out = new TextEncoder().encode(patched);
    }
  }
  return out;
};
